import styled, { css } from 'styled-components';

import { LinkButton } from '../../../../../../../components/Button';
import { LightTag } from '../../../../../../../components/Tags';
import colors from '../../../../../../../styles/colors';
import Tabs from '../../../../../Tabs';
import { StyledTab as PickerTab } from '../../../../../Tabs/styles';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 0;
`;

const EmptyText = styled.span`
  font-size: 13px;
  color: ${colors.shades.gamma2};
`;

const CategoryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
`;

const CategoryLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.shades.gamma6};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const ShowMoreButton = styled(LinkButton)`
  && {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 2px;
    color: ${colors.base.secondary};
    text-decoration: none;
    &:hover {
      text-decoration: none;
      opacity: 0.8;
    }
  }
`;

const PickerPanel = styled.div`
  border: 1px solid ${colors.shades.gamma8};
  border-radius: 6px;
  background: ${colors.base.alpha};
  overflow: hidden;
`;

const PickerSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${colors.shades.gamma8};
`;

const PickerList = styled.div`
  max-height: 280px;
  overflow-y: auto;
`;

const PickerListHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 4px;
`;

const PickerListHeader = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${colors.shades.gamma6};
`;

const PickerRow = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 14px 4px 6px;
  cursor: pointer;
  &:hover {
    background: ${colors.shades.secondary10};
  }
`;

const PickerRowName = styled.span`
  flex: 1;
  font-size: 13px;
  color: ${colors.shades.dark2};
`;

const PickerRowMeta = styled.span`
  font-size: 11px;
  color: ${colors.shades.gamma10};
  white-space: nowrap;
  margin-left: 8px;
`;

const PickerShowMore = styled.div`
  padding: 4px 0 4px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.base.secondary};
  &:hover {
    opacity: 0.8;
  }
`;

const PickerHelperText = styled.div`
  padding: 12px 14px;
  font-size: 13px;
  color: ${colors.shades.gamma10};
  font-style: italic;
`;

const PickerErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  font-size: 13px;
  color: ${colors.base.danger};
`;

const PickerTabsHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.shades.gamma8};
`;

const PickerTabs = styled(Tabs)`
  flex: 1;
  border-bottom: unset;
  min-height: unset;
  padding: 0;
  background-color: ${colors.shades.gamma5};

  ${PickerTab} {
    min-width: unset;
    min-height: unset;
    font-size: 13px;
    background-color: ${colors.shades.gamma5};

    &.Mui-selected {
      box-shadow: inset 0 -2px 0 ${colors.shades.secondary3};
    }
  }
`;

const AILoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: ${colors.shades.gamma10};
`;

const AIInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 14px;
`;

const AIHelperText = styled.div`
  font-size: 12px;
  color: ${({ $isError }) => ($isError ? colors.base.danger : colors.shades.gamma10)};
`;

const AIReviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 14px;
`;

const AIReviewHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.shades.dark2};
`;

const AIReviewChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const AIReviewChip = styled(LightTag)`
  cursor: pointer;

  ${({ $selected }) => ($selected ? css`
    background: ${colors.shades.secondary5};
    border-color: ${colors.base.secondary};
    color: ${colors.base.secondary};
  ` : css`
    opacity: 0.4;
    text-decoration: line-through;
  `)}
`;

const AIReviewActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export {
  Container,
  EmptyText,
  CategoryBlock,
  CategoryLabel,
  ChipRow,
  ShowMoreButton,
  PickerPanel,
  PickerSearchRow,
  PickerList,
  PickerListHeaderRow,
  PickerListHeader,
  PickerRow,
  PickerRowName,
  PickerRowMeta,
  PickerShowMore,
  PickerHelperText,
  PickerErrorRow,
  PickerTabsHeader,
  PickerTabs,
  AILoadingContainer,
  AIInputContainer,
  AIHelperText,
  AIReviewPanel,
  AIReviewHeader,
  AIReviewChipRow,
  AIReviewChip,
  AIReviewActions,
};
